import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../../services/apiAuth';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './ConversionFunnelAnalytics.css';

type DateRange = '7days' | '30days' | '90days' | 'year';

interface FunnelStage {
  name: string;
  count: number;
  percentage: number;
  dropOffRate: number;
}

interface BehaviorInsight {
  metric: string;
  value: number | string;
  description: string;
}

interface DropOffAnalysis {
  registeredButInactive: number;
  activeButNoProfile: number;
  profiledButNoClick: number;
  browsedButNoClick: number;
}

interface FunnelData {
  funnel: FunnelStage[];
  insights: BehaviorInsight[];
  clicksBySource: Record<string, number>;
  clicksByPlacement: Record<string, number>;
  topPages: Array<{ page: string; views: number }>;
  dropOffAnalysis: DropOffAnalysis;
  stageDistribution: Record<string, number>;
  summary: {
    totalProfiles: number;
    activeInPeriod: number;
    uniqueClickers: number;
    totalClicks: number;
    paidClicks: number;
    avgProfileCompletion: number;
    range: string;
  };
}

export default function ConversionFunnelAnalytics() {
  const { t } = useTranslation('admin');
  const [dateRange, setDateRange] = useState<DateRange>('30days');
  const [data, setData] = useState<FunnelData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(
        `${getApiBaseUrl()}/getConversionFunnel?range=${dateRange}`
      );
      if (response.ok) {
        const text = await response.text();
        if (!text) {
          setError('Empty response from server');
        } else {
          const result = JSON.parse(text);
          if (result.success) {
            setData(result.data);
          } else {
            setError('Failed to load funnel data');
          }
        }
      } else {
        setError('Failed to load funnel data');
      }
    } catch (err) {
      console.error('Error loading conversion funnel:', err);
      setError('Failed to load funnel data');
    }
    setLoading(false);
  }, [dateRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="funnel-loading">
        <div className="loading-spinner"></div>
        <p>Loading conversion funnel...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="funnel-error">
        <p>{error}</p>
        <button onClick={loadData} className="retry-btn">Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const maxCount = data.funnel[0]?.count || 1;
  const funnelColors = ['#8B5CF6', '#6366F1', '#3B82F6', '#0EA5E9', '#06B6D4'];

  return (
    <div className="conversion-funnel-analytics">
      {/* Header */}
      <div className="funnel-header">
        <h3>🔄 {t('analytics.funnel.title', 'Conversion Funnel')}</h3>
        <div className="date-range-selector">
          {(['7days', '30days', '90days', 'year'] as DateRange[]).map((range) => (
            <button
              key={range}
              className={`range-btn ${dateRange === range ? 'active' : ''}`}
              onClick={() => setDateRange(range)}
            >
              {t(`analytics.dateRange.${range}`, range)}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="funnel-summary-cards">
        <div className="summary-card">
          <div className="summary-icon">👥</div>
          <div className="summary-value">{data.summary.totalProfiles}</div>
          <div className="summary-label">Total Users</div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">🟢</div>
          <div className="summary-value">{data.summary.activeInPeriod}</div>
          <div className="summary-label">Active ({dateRange})</div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">🖱️</div>
          <div className="summary-value">{data.summary.uniqueClickers}</div>
          <div className="summary-label">Clickers</div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">📊</div>
          <div className="summary-value">{data.summary.avgProfileCompletion}%</div>
          <div className="summary-label">Avg Profile</div>
        </div>
      </div>

      {/* Visual Funnel */}
      <div className="funnel-visual">
        <h4>📉 Conversion Pipeline</h4>
        <div className="funnel-stages">
          {data.funnel.map((stage, index) => (
            <div key={stage.name} className="funnel-stage">
              <div className="stage-header">
                <span className="stage-name">{stage.name}</span>
                <span className="stage-count">{stage.count}</span>
              </div>
              <div className="stage-bar-container">
                <div
                  className="stage-bar"
                  style={{
                    width: `${Math.max((stage.count / maxCount) * 100, 3)}%`,
                    backgroundColor: funnelColors[index % funnelColors.length],
                  }}
                />
                <span className="stage-percentage">{stage.percentage}%</span>
              </div>
              {index < data.funnel.length - 1 && stage.dropOffRate > 0 && (
                <div className="drop-off-indicator">
                  ↓ {stage.dropOffRate}% drop-off
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Drop-Off Analysis */}
      <div className="drop-off-section">
        <h4>🚪 Drop-Off Analysis</h4>
        <div className="drop-off-grid">
          <div className="drop-off-card">
            <div className="drop-off-value">{data.dropOffAnalysis.registeredButInactive}</div>
            <div className="drop-off-label">Registered but Inactive</div>
            <div className="drop-off-desc">Users who signed up but never returned in this period</div>
          </div>
          <div className="drop-off-card">
            <div className="drop-off-value">{data.dropOffAnalysis.browsedButNoClick}</div>
            <div className="drop-off-label">Browsed but No Click</div>
            <div className="drop-off-desc">Active users who never clicked an affiliate link</div>
          </div>
          <div className="drop-off-card">
            <div className="drop-off-value">{data.dropOffAnalysis.profiledButNoClick}</div>
            <div className="drop-off-label">Profiled but No Click</div>
            <div className="drop-off-desc">Completed profile but no affiliate engagement</div>
          </div>
        </div>
      </div>

      {/* Key Insights */}
      <div className="insights-section">
        <h4>💡 Key Insights</h4>
        <div className="insights-grid">
          {data.insights.map((insight) => (
            <div key={insight.metric} className="insight-card">
              <div className="insight-value">{insight.value}</div>
              <div className="insight-metric">{insight.metric}</div>
              <div className="insight-desc">{insight.description}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Click Source Breakdown */}
      {Object.keys(data.clicksBySource).length > 0 && (
        <div className="source-breakdown-section">
          <h4>🔗 Click Sources</h4>
          <div className="source-bars">
            {Object.entries(data.clicksBySource)
              .sort((a, b) => b[1] - a[1])
              .map(([source, count]) => {
                const sourceColors: Record<string, string> = {
                  amazon: '#FF9900',
                  coupang: '#E44232',
                  iherb: '#5B8930',
                  yesstyle: '#000',
                  direct: '#6366F1',
                };
                return (
                  <div key={source} className="source-row">
                    <div className="source-info">
                      <span className="source-name">{source}</span>
                      <span className="source-count">{count}</span>
                    </div>
                    <div className="source-bar-bg">
                      <div
                        className="source-bar-fill"
                        style={{
                          width: `${(count / data.summary.totalClicks) * 100}%`,
                          backgroundColor: sourceColors[source] || '#9CA3AF',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Top Pages */}
      {data.topPages.length > 0 && (
        <div className="top-pages-section">
          <h4>📄 Most Visited Pages</h4>
          <div className="top-pages-list">
            {data.topPages.slice(0, 8).map((page, index) => (
              <div key={page.page} className="page-row">
                <span className="page-rank">#{index + 1}</span>
                <span className="page-name">{page.page}</span>
                <span className="page-views">{page.views} views</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
